import React from "react";
import { motion } from "framer-motion";
import { Quote, Star } from "lucide-react";
import Chapter from "../site/Chapter";

const PORTRAITS = [
  "https://images.unsplash.com/photo-1560250097-0b93528c311a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0fGVufDB8fHx8MTc3ODc1NDIyMXww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBvcnRyYWl0fGVufDB8fHx8MTc3ODc1NDIyMXww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1624797432677-6f803a98acb3?crop=faces&fit=crop&w=800&h=800&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85",
];

const testimonials = [
  {
    quote:
      "The results were better than any of my employees had ever achieved before, and at a fraction of the cost. To this day, I'm still amazed by the professional work the A4 team delivers — the level of accuracy, structure, and commitment is something I wasn't used to, even from vendors closer to home. The hit rate on candidate quality has been well over 80%, and it's only improved from there.",
    name: "Edgar",
    role: "Owner of a German Staffing Firm",
    portrait: PORTRAITS[0],
    highlight: "80%+ candidate quality hit rate",
  },
  {
    quote:
      "Sahil's ability to understand our company's needs, combined with a proactive approach to talent acquisition, was truly commendable. The proficiency, dedication, and seamless communication ensured our projects ran smoothly. I'd highly recommend A4 Assist to any company seeking RPO or virtual assistant services.",
    name: "Eva",
    role: "Operations Head for a SaaS Startup",
    portrait: PORTRAITS[1],
    highlight: "Proactive, seamless communication",
  },
  {
    quote:
      "Sahil and his team are particularly strong in research, lead generation, and the technology tools that make outreach actually work. He often brought ideas to improve our process and efficiency — competent, reliable, and professional throughout. I highly recommend them.",
    name: "Joe",
    role: "Owner of an Insurance Agency",
    portrait: PORTRAITS[2],
    highlight: "Research, lead gen & process ideas",
  },
];

export default function HomeTestimonials({ chapterNumber = "07" }) {
  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <Chapter
          number={chapterNumber}
          eyebrow="What It's Like to Work With Us"
          title={
            <>
              Real words from{" "}
              <span className="font-serif italic">real</span> clients.
            </>
          }
        />

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {testimonials.map((t, i) => (
            <motion.figure
              key={t.name}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              data-testid={`testimonial-card-${i}`}
              className="relative rounded-2xl border border-slate-800 bg-[#0A1325]/70 p-7 hover:border-[#00E5FF]/40 transition-colors flex flex-col justify-between"
            >
              <Quote
                size={30}
                className="text-[#00E5FF]/40 absolute top-6 right-6"
              />
              <div className="flex items-center gap-1 text-[#A3FF5E]">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} size={13} fill="currentColor" stroke="none" />
                ))}
              </div>
              <blockquote className="pull-quote mt-5 text-xl leading-snug text-white/95">
                “{t.quote}”
              </blockquote>
              <div className="mt-6 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/20 self-start font-semibold">
                {t.highlight}
              </div>
              <figcaption className="mt-6 pt-5 border-t border-slate-800 flex items-center gap-3">
                <div className="portrait-spotlight h-11 w-11 rounded-full overflow-hidden">
                  <img
                    src={t.portrait}
                    alt={t.name}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div>
                  <div className="font-display text-sm font-semibold text-white">
                    {t.name}
                  </div>
                  <div className="text-xs text-slate-500">{t.role}</div>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}

export { testimonials };
